/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ClassDistribution,
    ClassNames,
    ConfusionClasses,
    ConfusionEntities,
    EntityDistribution,
    EntityNames,
    InsufficientClasses,
    InsufficientEntities,
    LabelOrDocumentDistributionPerDataSet,
    ModelGuidanceReportSwagger,
    UnbalancedClasses,
    UnbalancedEntities
} from 'src/customText/core/services/modelGuidance/customTextModelGuidanceSwaggers';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';

export type LabelDistribution = {
    labelName: string;
    labelsDistribution: LabelOrDocumentDistributionPerDataSet;
    documentsDistribution: LabelOrDocumentDistributionPerDataSet;
};

export const customTextModalGuidanceSwaggersToStore = (modelName: string, swagger: ModelGuidanceReportSwagger) => {
    if (swagger === null) {
        return null;
    }

    const { projectKind, modelGuidance, distribution } = swagger;
    const missingLabels: string[] =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? (modelGuidance.missingLabels as EntityNames).entities
            : (modelGuidance.missingLabels as ClassNames).classes;

    const confusionLabels =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? (modelGuidance.confusingLabels as ConfusionEntities).confusingEntities
            : (modelGuidance.confusingLabels as ConfusionClasses).confusingClasses;

    const insufficientExamples =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? (modelGuidance.insufficientExamples as InsufficientEntities).entities
            : (modelGuidance.insufficientExamples as InsufficientClasses).classes;

    const testUnbalancedLabels =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? (modelGuidance.unbalancedLabelsDistribution.Test as UnbalancedEntities).entities
            : (modelGuidance.unbalancedLabelsDistribution.Test as UnbalancedClasses).classes;

    const trainUnbalancedLabels =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? (modelGuidance.unbalancedLabelsDistribution.Train as UnbalancedEntities).entities
            : (modelGuidance.unbalancedLabelsDistribution.Train as UnbalancedClasses).classes;

    const labelsDistribution: LabelDistribution[] =
        projectKind === 'CustomEntityRecognition' || projectKind === 'CustomHealthcare'
            ? (distribution as EntityDistribution).entities.map(entity => ({
                  labelName: entity.enityName,
                  labelsDistribution: entity.labelsDistribution,
                  documentsDistribution: entity.documentsDistribution
              }))
            : (distribution as ClassDistribution).classes.map(_class => ({
                  labelName: _class.className,
                  documentsDistribution: null,
                  labelsDistribution: _class.labelsDistribution
              }));

    return new ModelGuidanceStore({
        modelName,
        projectKind,
        missingLabels,
        confusionLabels,
        labelsDistribution,
        insufficientExamples,
        testUnbalancedLabels,
        trainUnbalancedLabels,
        modelDistributionPerDataSet: distribution.labelsDistribution,
        unbalancedDataDistribution: modelGuidance.unbalancedDataDistribution,
        testMinimumThreshold: modelGuidance.unbalancedLabelsDistribution.Test.minimumThreshold,
        trainMinimumThreshold: modelGuidance.unbalancedLabelsDistribution.Train.minimumThreshold,
        minimumNumberOfRequiredExamples: modelGuidance.insufficientExamples.minimumNumberOfRequiredExamples
    });
};
